import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { TabsRoot, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LayoutDashboard, List, Calendar } from 'lucide-react';
import CompactHeader from '@/components/layout/CompactHeader';
import GoalsWidget from '@/components/widgets/GoalsWidget';
import ServicesWidget from '@/components/widgets/ServicesWidget';
import AttendanceWidget from '@/components/widgets/AttendanceWidget';
import ImplementationsWidget from '@/components/widgets/ImplementationsWidget';
import ClientsWidget from '@/components/widgets/ClientsWidget';
import DailyAnalysisWidget from '@/components/widgets/DailyAnalysisWidget';
import ListsView from '@/components/views/ListsView';
import TimelineView from '@/components/views/TimelineView';
import AttendanceStatusChart from '@/components/charts/AttendanceStatusChart';
import ImplementationStatusChart from '@/components/charts/ImplementationStatusChart';
import GoalsStatusChart from '@/components/charts/GoalsStatusChart';
import ServicesClientChart from '@/components/charts/ServicesClientChart';
import { useAuth } from '@/contexts/AuthContext';
import { dataStorage } from '@/lib/dataStorage';
import { useToast } from '@/components/ui/use-toast';
import { useAuditLog } from '@/contexts/AuditLogContext';
import CreateEditModal from '@/components/modals/CreateEditModal';
import DeleteConfirmationModal from '@/components/modals/DeleteConfirmationModal';
import ClientsModal from '@/components/modals/ClientsModal';

// Helper function for API URLs
const getApiUrl = (endpoint) => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api';
  return `${baseUrl}${endpoint}`;
};

const DashboardPage = () => {
    // Client CRUD Handlers
    const handleCreateClient = () => {
      setSelectedClient(null);
      setIsClientsModalOpen(true);
    };

    const handleEditClient = (client) => {
      setSelectedClient(client);
      setIsClientsModalOpen(true);
    };

    const handleDeleteClient = async (client) => {
      if (!window.confirm(`Excluir cliente ${client.nome}?`)) return;
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(getApiUrl(`/clientes/${client.id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erro ao excluir cliente');
        }
        await fetchClients();
        toast({ title: "Sucesso", description: "Cliente excluído com sucesso." });
      } catch (error) {
        toast({ title: "Erro", description: error.message || "Falha ao excluir o cliente.", variant: "destructive" });
      }
    };

    const handleSaveClient = async (form) => {
      try {
        const token = localStorage.getItem('auth_token');
        const method = selectedClient ? 'PUT' : 'POST';
        const url = selectedClient
          ? getApiUrl(`/clientes/${selectedClient.id}`)
          : getApiUrl('/clientes');
        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(form),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erro ao salvar cliente');
        }
        await fetchClients();
        setIsClientsModalOpen(false);
        toast({ title: "Sucesso", description: "Cliente salvo com sucesso." });
      } catch (error) {
        toast({ title: "Erro", description: error.message || "Falha ao salvar o cliente.", variant: "destructive" });
      }
    };
  const { user, canDeleteCards } = useAuth();
  const { toast } = useToast();
  const { addLog } = useAuditLog();

  // Data State
  const [goals, setGoals] = useState([]);
  const [services, setServices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [implementations, setImplementations] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [clients, setClients] = useState([]);

  // Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState('');
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Initial Data Load
  useEffect(() => {
    fetchGoals();
    fetchServices();
    fetchTickets();
    fetchImplementations();
    fetchStatusTypes();
    fetchClients();
  }, []);
  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/clientes'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar clientes');
      const data = await response.json();
      setClients(data);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const fetchGoals = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/metas'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar metas');
      const data = await response.json();
      // Map backend fields to frontend widget shape
      const mapped = data.map(m => ({
        id: m.id,
        name: m.titulo,
        description: m.descricao,
        openingDate: m.data_inicio,
        dueDate: m.data_fim,
        lifecycleStatus: m.status || 'Planejamento',
        current: m.progresso_atual !== undefined ? parseFloat(m.progresso_atual) : (m.current || 0),
        target: m.progresso_objetivo !== undefined ? parseFloat(m.progresso_objetivo) : (m.target || 100),
        assignedTo: m.responsavel || null,
        empresa_id: m.empresa_id,
      }));
      setGoals(mapped);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/servicos'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar serviços');
      const data = await response.json();
      // Mapear campos do backend para o frontend mantendo dados originais
      const mapped = data.map(s => ({
        ...s,
        id: s.id,
        name: s.nome,
        description: s.descricao,
        cliente: s.cliente || null,
        cliente_id: s.cliente_id,
        openingDate: s.data_abertura || (s.created_at ? s.created_at.split('T')[0] : ''),
        dueDate: s.prazo || s.data_prazo || '',
        lifecycleStatus: s.status || 'Aberto',
        empresa_id: s.empresa_id,
        responsavel: s.responsavel || '',
        progresso_atual: s.progresso_atual || 0,
        progresso_objetivo: s.progresso_objetivo || 1,
      }));
      setServices(mapped);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/atendimentos'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar atendimentos');
      const data = await response.json();
      // Mapear campos do backend para o frontend
      const mapped = data.map(a => ({
        id: a.id,
        title: a.titulo,
        description: a.descricao,
        priority: a.prioridade,
        openingDate: a.data_abertura,
        dueDate: a.prazo,
        assignedTo: a.responsavel || '',
        lifecycleStatus: a.status === 'concluido' ? 'Concluído' : 'Aberto',
        empresa_id: a.empresa_id,
      }));
      setTickets(mapped);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const fetchImplementations = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/implementacoes'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar implementações');
      const data = await response.json();
      // Mapear campos do backend para o frontend
      const mapped = data.map(i => ({
        id: i.id,
        name: i.titulo,
        description: i.descricao,
        openingDate: i.data_abertura,
        dueDate: i.prazo,
        lifecycleStatus: i.status === 'concluido' ? 'Concluído' : 'Aberto',
        progress: i.progresso || 0,
        client: i.cliente || '',
        assignedTo: i.responsavel || '',
        checklist: i.checklist || [],
        empresa_id: i.empresa_id,
      }));
      setImplementations(mapped);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const fetchStatusTypes = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl('/status-types'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Erro ao buscar tipos de status');
      const data = await response.json();
      setStatusTypes(data);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  // Drag and Drop Logic for Widgets
  const [widgetOrder, setWidgetOrder] = useState(['goals', 'services', 'attendance', 'implementations']);

  useEffect(() => {
    const savedOrder = localStorage.getItem(`widget_order_${user?.id}`);
    if (savedOrder) {
      try {
        setWidgetOrder(JSON.parse(savedOrder));
      } catch (e) {
        console.error('Error parsing widget order');
      }
    }
  }, [user]);

  const saveWidgetOrder = (newOrder) => {
    setWidgetOrder(newOrder);
    localStorage.setItem(`widget_order_${user?.id}`, JSON.stringify(newOrder));
  };

  const handleDragStart = (e, widgetId) => {
    e.dataTransfer.setData('widgetId', widgetId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetWidgetId) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('widgetId');
    if (!draggedId || draggedId === targetWidgetId) return;

    const newOrder = [...widgetOrder];
    const fromIndex = newOrder.indexOf(draggedId);
    const toIndex = newOrder.indexOf(targetWidgetId);

    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedId);

    saveWidgetOrder(newOrder);
  };

  // Inline Update Handlers
  const handleUpdate = async (type, id, field, value, name, oldValue) => {
    try {
      const token = localStorage.getItem('auth_token');
      let endpoint = '';

      // Map types to endpoints
      switch (type) {
        case 'goals':
          endpoint = 'metas';
          break;
        case 'services':
          endpoint = 'servicos';
          break;
        case 'attendance':
          endpoint = 'atendimentos';
          break;
        case 'implementations':
          endpoint = 'implementacoes';
          break;
        default:
          throw new Error('Tipo desconhecido');
      }

      // Prepare update data (map frontend field names to backend fields)
      let updateData = {};
      if (type === 'goals') {
        // Sempre envie titulo e status para evitar erro 422
        const goal = goals.find(g => g.id === id);
        switch (field) {
          case 'name':
            updateData.titulo = value;
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            break;
          case 'description':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.descricao = value;
            break;
          case 'openingDate':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.data_inicio = value;
            break;
          case 'dueDate':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.data_fim = value;
            break;
          case 'lifecycleStatus':
            updateData.status = value;
            updateData.titulo = name || goal?.name || '';
            break;
          case 'assignedTo':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.responsavel = value;
            break;
          case 'current':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.progresso_atual = value;
            updateData.progresso_objetivo = goal?.target || 100;
            break;
          case 'target':
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData.progresso_objetivo = value;
            updateData.progresso_atual = goal?.current || 0;
            break;
          default:
            updateData.titulo = goal?.name || '';
            updateData.status = goal?.lifecycleStatus || 'Aberto';
            updateData[field] = value;
            break;
        }
      } else if (type === 'services') {
        const service = services.find(s => s.id === id);
        switch (field) {
          case 'name': updateData.nome = value; break;
          case 'description': updateData.descricao = value; break;
          case 'client': updateData.cliente = value; break;
          case 'openingDate': updateData.data_abertura = value; break;
          case 'dueDate': updateData.prazo = value; break;
          case 'lifecycleStatus': updateData.status = value; break;
          case 'assignedTo': updateData.responsavel = value; break;
          case 'progresso_atual': updateData.progresso_atual = value; break;
          case 'progresso_objetivo': updateData.progresso_objetivo = value; break;
          default: updateData[field] = value; break;
        }
      } else if (type === 'attendance') {
        const ticket = tickets.find(t => t.id === id);
        switch (field) {
          case 'title':
            updateData.titulo = value;
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          case 'description':
            updateData.titulo = ticket?.title || '';
            updateData.descricao = value;
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          case 'openingDate':
            updateData.titulo = ticket?.title || '';
            updateData.data_abertura = value;
            updateData.prioridade = ticket?.priority || 'media';
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          case 'dueDate':
            updateData.titulo = ticket?.title || '';
            updateData.prazo = value;
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          case 'lifecycleStatus':
            updateData.titulo = ticket?.title || '';
            updateData.status = value === 'Concluído' ? 'concluido' : 'aberto';
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            break;
          case 'assignedTo':
            updateData.titulo = ticket?.title || '';
            updateData.responsavel = value;
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          case 'priority':
            updateData.titulo = ticket?.title || '';
            updateData.prioridade = value;
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            break;
          default:
            updateData.titulo = ticket?.title || '';
            updateData.prioridade = ticket?.priority || 'media';
            updateData.data_abertura = ticket?.openingDate || new Date().toISOString().split('T')[0];
            updateData.prazo = ticket?.dueDate || new Date().toISOString().split('T')[0];
            updateData.status = ticket?.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto';
            updateData[field] = value;
            break;
        }
      } else if (type === 'implementations') {
        const impl = implementations.find(i => i.id === id);
        switch (field) {
          case 'name': 
            updateData.titulo = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : (impl?.lifecycleStatus === 'Aberto' ? 'planejamento' : 'planejamento');
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'description': 
            updateData.titulo = impl?.name || '';
            updateData.descricao = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'openingDate': 
            updateData.titulo = impl?.name || '';
            updateData.data_abertura = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            break;
          case 'dueDate': 
            updateData.titulo = impl?.name || '';
            updateData.prazo = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'lifecycleStatus':
            updateData.titulo = impl?.name || '';
            updateData.status = value === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'assignedTo': 
            updateData.titulo = impl?.name || '';
            updateData.responsavel = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'progress': 
            updateData.titulo = impl?.name || '';
            updateData.progresso = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          case 'client': 
            updateData.titulo = impl?.name || '';
            updateData.cliente = value;
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            break;
          default: 
            updateData.titulo = impl?.name || '';
            updateData.status = impl?.lifecycleStatus === 'Concluído' ? 'concluido' : 'planejamento';
            updateData.data_abertura = impl?.openingDate || new Date().toISOString().split('T')[0];
            updateData[field] = value;
            break;
        }
      } else {
        updateData = { [field]: value };
      }

      // Handle special cases for status changes
      if (field === 'lifecycleStatus') {
        if (value === 'Concluído') {
          updateData.completedAt = new Date().toISOString();
        } else if (value === 'Aberto') {
          updateData.completedAt = null;
        }
      }

      const response = await fetch(getApiUrl(`/${endpoint}/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao atualizar item');
      }

      // Refresh data after update
      switch (type) {
        case 'goals':
          await fetchGoals();
          break;
        case 'services':
          await fetchServices();
          break;
        case 'attendance':
          await fetchTickets();
          break;
        case 'implementations':
          await fetchImplementations();
          break;
      }

      // Log the change in audit trail
      if (oldValue !== undefined && oldValue !== value) {
        addLog({
          userId: user.id,
          userName: user.name,
          entityId: id,
          fieldName: field,
          oldValue: oldValue,
          newValue: value
        });
      }

      toast({ title: "Sucesso", description: "Item atualizado com sucesso." });
    } catch (error) {
      console.error(error);
      toast({ title: "Erro", description: error.message || "Falha ao atualizar o item.", variant: "destructive" });
    }
  };

  const handleCreate = (type) => {
    setSelectedItem(null);
    setModalType(type);
    setIsEditModalOpen(true);
  };

  const handleEdit = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setIsEditModalOpen(true);
  };

  const handleDelete = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setIsDeleteModalOpen(true);
  };

  const onSaveItem = async (formData) => {
    try {
      const token = localStorage.getItem('auth_token');
      let endpoint = '';
      let method = selectedItem ? 'PUT' : 'POST';
      let url = '';

      // Map types to endpoints
      switch (modalType) {
        case 'goals':
          endpoint = 'metas';
          break;
        case 'services':
          endpoint = 'servicos';
          break;
        case 'attendance':
          endpoint = 'atendimentos';
          break;
        case 'implementations':
          endpoint = 'implementacoes';
          break;
        default:
          throw new Error('Tipo desconhecido');
      }

      url = selectedItem
        ? getApiUrl(`/${endpoint}/${selectedItem.id}`)
        : getApiUrl(`/${endpoint}`);

      // Map formData to backend fields
      let mappedData = { ...formData };
      switch (modalType) {
        case 'goals':
            mappedData = {
              titulo: formData.name,
              descricao: formData.description,
              data_inicio: formData.openingDate,
              data_fim: formData.dueDate,
              status: formData.lifecycleStatus || 'Aberto',
              responsavel: formData.assignedTo || null,
              progresso_atual: formData.current || 0,
              progresso_objetivo: formData.target || 100,
            };
          break;
        case 'services':
          mappedData = {
            nome: formData.name,
            descricao: formData.description,
            cliente_id: formData.client || null,
            data_abertura: formData.openingDate || new Date().toISOString().split('T')[0],
            prazo: formData.dueDate || null,
            responsavel: formData.assignedTo || null,
            status: formData.lifecycleStatus || 'Aberto',
            progresso_atual: formData.progresso_atual || 0,
            progresso_objetivo: formData.progresso_objetivo || 1,
          };
          break;
        case 'attendance':
          {
          const normalizedPriority = (formData.priority || 'media') === 'medium' ? 'media' : (formData.priority || 'media');
          mappedData = {
            titulo: formData.title,
            descricao: formData.description,
            prioridade: normalizedPriority,
            data_abertura: formData.openingDate || new Date().toISOString().split('T')[0],
            prazo: formData.dueDate || new Date().toISOString().split('T')[0],
            responsavel: formData.assignedTo || null,
            status: formData.lifecycleStatus === 'Concluído' ? 'concluido' : 'aberto',
          };
          }
          break;
        case 'implementations':
          mappedData = {
            titulo: formData.name,
            descricao: formData.description,
            data_abertura: formData.openingDate || new Date().toISOString().split('T')[0],
            prazo: formData.dueDate,
            responsavel: formData.assignedTo || null,
            status: formData.lifecycleStatus === 'Concluído' ? 'concluido' : (formData.status === 'em_andamento' ? 'em_andamento' : 'planejamento'),
            progresso: formData.progress || 0,
            checklist: formData.checklist || null,
            cliente: formData.client || null,
          };
          break;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(mappedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao salvar item');
      }

      // Refresh data after save
      switch (modalType) {
        case 'goals':
          await fetchGoals();
          break;
        case 'services':
          await fetchServices();
          break;
        case 'attendance':
          await fetchTickets();
          break;
        case 'implementations':
          await fetchImplementations();
          break;
      }

      setIsEditModalOpen(false);
      toast({ title: "Sucesso", description: "Item salvo com sucesso." });
    } catch (error) {
      console.error(error);
      toast({ title: "Erro", description: error.message || "Falha ao salvar o item.", variant: "destructive" });
    }
  };

  const onConfirmDelete = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      let endpoint = '';

      // Map types to endpoints
      switch (modalType) {
        case 'goals':
          endpoint = 'metas';
          break;
        case 'services':
          endpoint = 'servicos';
          break;
        case 'attendance':
          endpoint = 'atendimentos';
          break;
        case 'implementations':
          endpoint = 'implementacoes';
          break;
        default:
          throw new Error('Tipo desconhecido');
      }

      const response = await fetch(getApiUrl(`/${endpoint}/${selectedItem.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao excluir item');
      }

      // Refresh data after delete
      switch (modalType) {
        case 'goals':
          await fetchGoals();
          break;
        case 'services':
          await fetchServices();
          break;
        case 'attendance':
          await fetchTickets();
          break;
        case 'implementations':
          await fetchImplementations();
          break;
      }

      addLog({ userId: user.id, userName: user.name, entityId: selectedItem.id, fieldName: 'Item', oldValue: 'Existing', newValue: 'Deleted' });
      setIsDeleteModalOpen(false);
      toast({ title: "Sucesso", description: "Item excluído com sucesso." });
    } catch (error) {
      console.error(error);
      toast({ title: "Erro", description: error.message || "Falha ao excluir o item.", variant: "destructive" });
    }
  };

  const renderWidget = (id) => {
    switch (id) {
      case 'goals':
        return <GoalsWidget goals={goals} statusTypes={statusTypes} onCreate={() => handleCreate('goals')} onUpdate={(id, field, val, name) => handleUpdate('goals', id, field, val, name)} onEdit={(i) => handleEdit(i, 'goals')} onDelete={(i) => handleDelete(i, 'goals')} dragHandleProps={{ draggable: true, onDragStart: (e) => handleDragStart(e, 'goals') }} />;
      case 'services':
              return <ServicesWidget services={services} statusTypes={statusTypes} onCreate={() => handleCreate('services')} onUpdate={(id, field, val) => handleUpdate('services', id, field, val)} onEdit={(i) => handleEdit(i, 'services')} onDelete={(i) => handleDelete(i, 'services')} dragHandleProps={{ draggable: true, onDragStart: (e) => handleDragStart(e, 'services') }} />;
      case 'attendance':
        return <AttendanceWidget tickets={tickets} statusTypes={statusTypes} onCreate={() => handleCreate('attendance')} onUpdate={(id, field, val) => handleUpdate('attendance', id, field, val)} onEdit={(i) => handleEdit(i, 'attendance')} onDelete={(i) => handleDelete(i, 'attendance')} dragHandleProps={{ draggable: true, onDragStart: (e) => handleDragStart(e, 'attendance') }} />;
      case 'implementations':
        return <ImplementationsWidget implementations={implementations} statusTypes={statusTypes} onCreate={() => handleCreate('implementations')} onUpdate={(id, field, val) => handleUpdate('implementations', id, field, val)} onEdit={(i) => handleEdit(i, 'implementations')} onDelete={(i) => handleDelete(i, 'implementations')} dragHandleProps={{ draggable: true, onDragStart: (e) => handleDragStart(e, 'implementations') }} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Painel - Dashboard Center</title>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        <CompactHeader />

        <main className="p-4 md:p-8 max-w-[1800px] mx-auto space-y-8">
          
          {/* Header & Daily Analysis */}
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white mb-2">Visão Geral</h1>
            <DailyAnalysisWidget 
              goals={goals} 
              services={services} 
              attendance={tickets} 
              implementations={implementations} 
            />
          </div>

          <TabsRoot defaultValue="overview" className="w-full">
            <div className="border-b border-white/10 mb-8">
              <TabsList className="bg-transparent p-0 gap-8 h-12">
                <TabsTrigger value="overview" className="data-[state=active]:bg-transparent data-[state=active]:text-orange-500 data-[state=active]:border-b-2 data-[state=active]:border-orange-500 rounded-none px-0 pb-3 text-lg text-gray-400 font-medium">
                  <LayoutDashboard className="w-5 h-5 mr-2" />
                  Gráficos & Widgets
                </TabsTrigger>
                <TabsTrigger value="lists" className="data-[state=active]:bg-transparent data-[state=active]:text-orange-500 data-[state=active]:border-b-2 data-[state=active]:border-orange-500 rounded-none px-0 pb-3 text-lg text-gray-400 font-medium">
                  <List className="w-5 h-5 mr-2" />
                  Listas
                </TabsTrigger>
                <TabsTrigger value="timeline" className="data-[state=active]:bg-transparent data-[state=active]:text-orange-500 data-[state=active]:border-b-2 data-[state=active]:border-orange-500 rounded-none px-0 pb-3 text-lg text-gray-400 font-medium">
                  <Calendar className="w-5 h-5 mr-2" />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="clientes" className="data-[state=active]:bg-transparent data-[state=active]:text-orange-500 data-[state=active]:border-b-2 data-[state=active]:border-orange-500 rounded-none px-0 pb-3 text-lg text-gray-400 font-medium">
                  <span className="w-5 h-5 mr-2 inline-block">👥</span>
                  Clientes
                </TabsTrigger>
              </TabsList>
                        {/* Clientes Tab */}
                        <TabsContent value="clientes">
                          <ClientsWidget
                            clients={clients}
                            onCreate={handleCreateClient}
                            onEdit={handleEditClient}
                            onDelete={handleDeleteClient}
                          />
                        </TabsContent>
                  <ClientsModal
                    isOpen={isClientsModalOpen}
                    onClose={() => setIsClientsModalOpen(false)}
                    onSave={handleSaveClient}
                    client={selectedClient}
                  />
            </div>

            {/* Overview Tab (Existing Charts + Widgets) */}
            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 auto-rows-fr">
               <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="min-h-[420px]">
                   <GoalsStatusChart data={goals} statusTypes={statusTypes} />
                </motion.div>
               <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }} className="min-h-[420px]">
                   <ServicesClientChart data={services} statusTypes={statusTypes} />
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} className="min-h-[420px]">
                   <ImplementationStatusChart data={implementations} statusTypes={statusTypes} />
                 </motion.div>
               <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }} className="min-h-[420px]">
                 <AttendanceStatusChart data={tickets} statusTypes={statusTypes} />
                </motion.div>
              </div>

              <div className="border-t border-white/10 pt-8">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {widgetOrder.map((widgetId) => (
                    <motion.div
                      key={widgetId}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      className="min-h-[500px]"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, widgetId)}
                    >
                      {renderWidget(widgetId)}
                    </motion.div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Lists Tab */}
            <TabsContent value="lists">
              <ListsView 
                goals={goals} 
                services={services} 
                attendance={tickets} 
                implementations={implementations}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline">
              <TimelineView 
                goals={goals} 
                services={services} 
                attendance={tickets} 
                implementations={implementations}
                onEdit={handleEdit}
              />
            </TabsContent>
          </TabsRoot>

        </main>
      </div>

      <CreateEditModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={onSaveItem}
        item={selectedItem}
        type={modalType}
        statusTypes={statusTypes}
        clients={modalType === 'services' || modalType === 'implementations' ? clients : []}
      />

      <DeleteConfirmationModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={onConfirmDelete}
        itemName={selectedItem?.name || selectedItem?.title}
        itemType={modalType}
      />
    </>
  );
};

export default DashboardPage;